import { Component, Input, ViewChild } from "@angular/core";
import { MatTree, MatTreeNestedDataSource } from "@angular/material/tree";
import { ThesaurusEntry } from "@interfaces/thesaurus-entry.interface";
import { ThesaurusPath } from "@interfaces/thesaurus-path.interface";
import { TermsService } from "@terms-service/terms.service";
import { ThesaurusService } from "@thesaurus-service/thesaurus.service";
import { UtilitiesService } from "@utilities-service/utilities.service";
import { EventsService } from "app/services/events/events.service";
import { firstValueFrom } from "rxjs";

@Component({
    selector: 'app-thesaurus-root',
    templateUrl: './thesaurus-root.component.html',
    styleUrls: ['./thesaurus-root.component.scss'],
    standalone: false
})
export class ThesaurusRootComponent {
  @ViewChild('tree', { static: false })
  thesTree!: MatTree<ThesaurusEntry>;
  @Input() thesaurusName!: string;
  @Input() thesaurusId: string = '';
  @Input() token!: string;
  @Input() entryOrdinal: number = 0;
  @Input() isMajor: boolean = false;
  @Input() isExplode: boolean = false;
  entry!: ThesaurusEntry;
  useTermsInstead!: ThesaurusEntry;
  isUseForTerm: boolean = false;
  hasNotes: boolean = false;
  hasBroaderTerms: boolean = false;
  hasNarrowerTerms: boolean = false;
  subscription: any;
  display: boolean = true;
  thesaurusDataSource: MatTreeNestedDataSource<ThesaurusEntry>;
  childrenAccessor = (node: ThesaurusEntry) => node.narrowerTerms ?? [];
  loadingNodes: ThesaurusEntry[] = [];


  constructor(
    private thesaurusService: ThesaurusService,
    public termsService: TermsService,
    private eventsService: EventsService,
    public utilitiesService: UtilitiesService
  ) {
    this.thesaurusDataSource = new MatTreeNestedDataSource<ThesaurusEntry>();
  }

  async ngOnInit(): Promise<void> {
    this.showIndexSpinner();
    await this.getRootObject(this.thesaurusId, this.entryOrdinal, this.token).then(
        entry => {
            this.thesaurusDataSource.data = [entry];
            this.thesTree.expandAll();
            this.hideIndexSpinner();
        }
    );
  }

  async getRootObject(thesaurusId: string, entryOrdinal: number, token: string): Promise<ThesaurusEntry> {
      this.entry = await firstValueFrom(this.thesaurusService.getthesaurusRoot(thesaurusId, token));
      this.entry.name = "Root of " + this.thesaurusName;
      this.hasNotes = this.checkIfEntryHasNotes(this.entry);
      this.hasNarrowerTerms = (this.entry.narrowerTerms ?? []).length > 0;

      if (this.hasNarrowerTerms) {
        this.entry.narrowerTerms = await this.populateNarrowerterms(this.useTermsInstead? this.useTermsInstead: this.entry, thesaurusId, token);
      }
      return this.entry;
    }

    hasChild = (_: number, node: ThesaurusEntry) => !!node?.narrowerTerms && node?.narrowerTerms.length > 0;

    isLeaf = (_: number, node: ThesaurusEntry) => !this.hasChild(_, node);

    isRootNode = (node: ThesaurusEntry) => node?.termOrdinal === this.entry?.termOrdinal;

    checkIfEntryHasNotes(entry: ThesaurusEntry): boolean {
    return !!entry.description || !!entry.useTermFor?.length || !!entry.relatedTerms?.length;
    }

    isLoaded = (node: ThesaurusEntry) => !!node?.narrowerTerms && node?.narrowerTerms.length > 0 && !!node?.narrowerTerms[0]?.broaderTerms;

    async handleNodeChange(isExpanded: boolean, node: ThesaurusEntry) {
        if (!this.isLoaded(node)) {
            this.disableTermsWrapper();
            this.showEntrySpinner(node);
            this.thesTree.collapse(node);
            const children = await this.populateNarrowerterms(node, this.thesaurusId, this.token);
            node.narrowerTerms = children;
            this.refreshTree(node);
        }
    }

    refreshTree(node: ThesaurusEntry) {
      let data = this.thesaurusDataSource.data;
      this.thesaurusDataSource.data = [];
      this.thesaurusDataSource.data = data;
      this.hideEntrySpinner(node);
      this.enableTermsWrapper();
      this.thesTree.expand(node);
    }

  async populateNarrowerterms(entry: ThesaurusEntry, thesaurusId: string, token: string): Promise<ThesaurusEntry[]> {
    let entryNT : ThesaurusEntry[] = [];
    entry.narrowerTerms = this.filterNarrowerTermsByPath(entry.narrowerTerms ?? [], entry.tn ?? '');
    if (entry.narrowerTerms) {
      for (let term of entry.narrowerTerms) {
        await firstValueFrom(this.thesaurusService.getthesaurusSingleEntry(thesaurusId, term.termOrdinal, token)).then(
          narrowerTerm => {
            if (narrowerTerm.paths && narrowerTerm.paths.length > 1) {
              let selectedPath : ThesaurusPath = {
                tn: "",
                terms: []
              };
              for (let p of narrowerTerm.paths) {
                if (entry.paths?.[0] && p.tn) {
                    if (p.tn.indexOf(entry.paths[0].tn) === 0) {
                        selectedPath = p;
                        break;
                    }
                } else if ( this.comparePathTerms(entry.paths?.[0], p) ) {
                    selectedPath = p;
                    break;
                }
              }
              narrowerTerm.paths = [selectedPath];
              narrowerTerm.narrowerTerms = this.filterNarrowerTermsByPath(narrowerTerm.narrowerTerms ?? [], narrowerTerm.paths?.[0]?.tn ?? '');

            }
            if (entryNT.findIndex(e => e.termOrdinal === narrowerTerm.termOrdinal) === -1) {
              entryNT.push(narrowerTerm);
              this.hasNotes = this.hasNotes || this.checkIfEntryHasNotes(narrowerTerm);
            }
      });
      }
    }
    this.hasNarrowerTerms = entryNT.length > 0;
    return entryNT;
  }

  filterNarrowerTermsByPath(terms: ThesaurusEntry[], tn: string): ThesaurusEntry[] {
    if (tn) {
      return terms.filter(term => {
        return term.tn === tn;
      });
    }
    return terms;
  }

    showEntrySpinner(node: ThesaurusEntry) {
      let nodeButton = document.getElementById("mat-tree-node-" + node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
      nodeButton?.classList.add("hide-node-button");
      this.eventsService.emitShowEntrySpinner(node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
   }

   hideEntrySpinner(node: ThesaurusEntry) {
      let nodeButton = document.getElementById("mat-tree-node-" + node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
      nodeButton?.classList.remove("hide-node-button");
      this.eventsService.emitHideEntrySpinner(node.termOrdinal + (node.paths?.[0] ? this.utilitiesService.getPathIdentifier(node.paths) : ""));
   }
   
  showIndexSpinner() {
    this.eventsService.emitShowIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

  hideIndexSpinner() {
    this.eventsService.emitHideIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(false);
  }

  disableTermsWrapper() {
    let treeRoot = document.getElementById("termsWrapper");
    treeRoot?.classList.add("disable-input");
  }

  enableTermsWrapper() {
    let treeRoot = document.getElementById("termsWrapper");
    treeRoot?.classList.remove("disable-input");
  }

  /**
   * Returns `true` if both paths contain exactly the same terms (matched by `termOrdinal`),
   * regardless of order.
   */
  comparePathTerms(path1: ThesaurusPath | undefined, path2: ThesaurusPath | undefined): boolean {
    if (!path1 || !path2) {
        return false;
    }
    if (path1.terms.length !== path2.terms.length - 1) {
        return false;
    }
    for (let i = 0; i < path1.terms.length; i++) {
        if (path1.terms[i].termOrdinal === path2.terms[i].termOrdinal) {
            continue;
        }
        else 
            return false;
    }
    return true;
  }

}
